const sectionStyle = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "60px 20px"
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "20px"
};

const cardStyle = {
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: "20px",
  background: "#fff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease"
};

const hoverEffect = (e, enter = true) => {
  e.currentTarget.style.boxShadow = enter
    ? "0 15px 35px rgba(0,0,0,0.1)"
    : "0 10px 25px rgba(0,0,0,0.05)";
  e.currentTarget.style.transform = enter
    ? "translateY(-3px)"
    : "translateY(0)";
};

const AwsMachineLearningSpecialty = () => {
  return (
    <div>

      {/* HERO */}
      <div style={{ ...sectionStyle, paddingTop: "80px" }}>
        <h1 style={{ fontSize: "36px", marginBottom: "20px" }}>
          Designing Production-Ready Machine Learning Systems on AWS
        </h1>

        <p style={{ fontSize: "18px", color: "#555", maxWidth: "700px" }}>
          With the AWS Certified Machine Learning – Specialty certification, I design and implement
          end-to-end ML solutions — from data preparation to model deployment and production integration.
        </p>
      </div>


      {/* IMAGE */}
      <div style={{ ...sectionStyle, paddingTop: "0", textAlign: "center" }}>
        <img
          src="/images/awsMachineLearningBadge.png"
          alt="AWS Certified Machine Learning Specialty"
          style={{
            width: "100%",
            maxWidth: "280px",
            borderRadius: "12px",
            boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
          }}
        />

        <div style={{ fontSize: "14px", color: "#777", marginTop: "10px" }}>
          AWS Certified Machine Learning – Specialty
        </div>
      </div>


      {/* INTRO */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          This certification reflects deep expertise in building machine learning pipelines,
          deploying models at scale, and integrating ML into real-world systems.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          My approach focuses on practical implementation — ensuring that ML solutions are not only accurate,
          but also scalable, maintainable, and aligned with business goals.
        </p>
      </div>


      {/* CAPABILITIES 🔥 */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          Machine Learning Capabilities I Can Deliver
        </h2>

        <div style={gridStyle}>
          {[
            "Designing end-to-end ML pipelines from data ingestion to inference",
            "Preparing and transforming datasets for machine learning workflows",
            "Training, tuning, and evaluating machine learning models",
            "Deploying models into scalable AWS environments",
            "Implementing real-time and batch inference systems",
            "Designing MLOps workflows for model lifecycle management",
            "Monitoring model performance and data drift",
            "Integrating ML models with APIs and backend systems",
            "Optimizing ML workloads for cost and performance"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              ✓ {item}
            </div>
          ))}
        </div>
      </div>


      {/* USE CASES 🔥 */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "25px" }}>
          Where This Creates Real Value
        </h2>

        <div style={gridStyle}>
          {[
            "Predictive analytics and forecasting systems",
            "Recommendation engines and personalization",
            "Fraud detection and anomaly detection systems",
            "Intelligent document and data processing",
            "AI-enhanced applications and automation",
            "Data-driven decision support systems"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* MLOPS 🔥 */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          Production ML Approach (MLOps)
        </h2>

        <div style={gridStyle}>
          {[
            "Automated model training and deployment pipelines",
            "Versioning of models and datasets",
            "Continuous monitoring and performance tracking",
            "Handling model drift and retraining strategies",
            "Integration with CI/CD pipelines",
            "Ensuring scalability and reliability of ML systems"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* POSITIONING */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          Machine learning is most valuable when it is part of a well-designed system —
          not an isolated model.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          I focus on integrating ML into scalable cloud architectures, ensuring that models
          deliver consistent and measurable business outcomes.
        </p>
      </div>


      {/* CTA 🔥 */}
      <div style={{
        textAlign: "center",
        margin: "80px 0",
        padding: "50px 20px",
        background: "#fafafa"
      }}>
        <h2 style={{ marginBottom: "15px" }}>
          Integrate Machine Learning into Your AWS Systems
        </h2>

        <p style={{ color: "#555", marginBottom: "25px" }}>
          Let’s design and implement ML solutions tailored to your business needs.
        </p>

        <a
          href="/contact"
          style={{
            display: "inline-block",
            padding: "14px 32px",
            background: "#007bff",
            color: "#fff",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
          }}
        >
          Discuss Your ML Use Case →
        </a>
      </div>

    </div>
  );
};

export default AwsMachineLearningSpecialty;